<template>
  <dl class="flex flex-wrap gap-3 mb-3 md:flex-nowrap ">

    <div :class="[
      'flex flex-wrap justify-between w-full px-4 py-1 overflow-hidden rounded-lg shadow-md md:py-2 overflow state_unknown group',
      { 'cursor-pointer hover:bg-gray-200/70': canChangeTenant && !isLoadingTenantChange },
      { 'opacity-60': isLoadingTenantChange }
    ]" data-testid="device-info-tenant" @click="!isLoadingTenantChange && openChangeTenantModal()">
      <dt class="my-auto mr-2 text-sm xl:w-fit lg:w-full">{{ $t("device.header.tenant") }} </dt>
      <dd class="flex items-center gap-2 my-auto text-sm font-medium truncate lg:font-semibold lg:text-base">
        <template v-if="isLoadingTenantChange">
          <LoadingSpinner class="w-5 h-5 " size="h-5 w-5" />
        </template>
        <template v-else>
          {{ device.owner && device.owner.name ? device.owner.name : '-' }}
          <PencilIcon v-if="canChangeTenant" class="w-5 h-5 text-gray-500 cursor-pointer group-hover:text-gray-700" />
        </template>
      </dd>
    </div>

    <div data-testid="device-info-serial-number" class="flex flex-wrap justify-between w-full px-4 py-1 overflow-hidden rounded-lg shadow-md md:py-2 overflow state_unknown" ref="serialNumberContainer">
      <dt class="my-auto text-sm truncate xl:w-fit lg:w-full">{{ $t("device.header.serialNumber") }}</dt>
      <dd class="my-auto text-sm font-medium lg:font-semibold lg:text-base" v-if="device">{{ device.serialNumber }} </dd>
      <dd class="my-auto text-sm font-medium lg:font-semibold lg:text-base" v-else>-</dd>
    </div>

    <div data-testid="device-info-id" class="flex flex-wrap justify-between w-full px-4 py-1 overflow-hidden rounded-lg shadow-md md:py-2 overflow state_unknown">
      <dt class="my-auto text-sm truncate xl:w-fit lg:w-full">{{ $t("device.header.id") }}</dt>
      <dd class="my-auto text-sm font-medium lg:font-semibold lg:text-base">{{ getDeviceId(device) ? getDeviceId(device) : "-" }} </dd>
    </div>

    <div data-testid="device-info-firmware" class="flex flex-wrap justify-between w-full px-4 py-1 overflow-hidden rounded-lg shadow-md md:py-2 overflow state_unknown" ref="firmwareVersionContainer">
      <dt class="my-auto text-sm truncate xl:w-fit lg:w-full">{{ $t("device.header.firmware") }} </dt>
      <dd class="my-auto text-sm font-medium lg:font-semibold lg:text-base" v-if="device.firmware && device.firmware.version">{{ device.firmware.version }} </dd>
      <dd class="my-auto text-sm font-medium lg:font-semibold lg:text-base" v-else>-</dd>
    </div>

    <!-- uptime of the current month -->
    <div id="tooltipTriggerUptime" data-testid="device-info-uptime" class="flex flex-wrap justify-between w-full px-4 py-1 overflow-hidden rounded-lg shadow-md md:py-2 overflow state_unknown">
      <dt class="my-auto text-sm truncate xl:w-fit lg:w-full">{{ $t("device.header.uptimeMonth", { month: uptimeMonthName }) }}</dt>
      <dd class="flex items-center gap-1 my-auto text-sm font-medium lg:font-semibold lg:text-base">
        <template v-if="uptime">
          <span data-testid="device-uptime-hours">{{ formatNumber(uptime.hours) }} h</span>
        </template>
        <span v-else data-testid="device-uptime-empty">-</span>
      </dd>
    </div>
    <AlarmTooltip targetId="tooltipTriggerUptime" type="uptime" alert="" :device="device" :fallback="uptimeTooltip" />

  </dl>

  <div class="relative ">
    <dl class="relative top-0 flex flex-wrap justify-between w-full gap-3 lg:flex-nowrap">

      <!-- network -->
      <AlarmTooltip :targetId="'tooltipTriggerHighestNetwork'" type="network" :alert="getHighestAlert('network', device)" :device="device" />

      <div :class="[
        'rounded-lg overflow overflow-hidden shadow-md px-4 py-2 flex-auto h-full cursor-pointer w-full 2xl:flex-auto 2xl:w-full order-1 2xl:order-none fullHeightNetwork',
        ...stateColor(device, 'network')
      ]" data-testid="device-status-network" ref="networkStatusContainer">
        <div data-accordion="open">
          <div class="flex flex-wrap justify-between text-gray-800 bg-transparent" id="accordion-open-heading-network" data-accordion-target="#accordion-open-body-network" aria-expanded="false" aria-controls="accordion-open-body-network">
            <div class="flex justify-between w-full">
              <div class="justify-between block">
                <dt class="text-sm truncate lg:text-xs 2xl:text-sm">{{ $t("device.header.connectivityStatus") }}</dt>

                <div v-if="hasNetworkAlert" class="text-sm font-medium lg:font-semibold" ref="networkStatusHasAlert">
                  <p id='tooltipTriggerHighestNetwork' class="flex" v-html="getMostCriticalAlert('network', device)"></p>
                </div>

                <div v-else class="pr-5 mr-5 text-sm font-medium lg:font-semibold" ref="networkStatusNoAlert">
                  <span v-if="getOnlineStatus(device) == 'Online'" class="truncate">
                    Online via {{ getConnectionStatus(device) }}
                  </span>
                  <span v-else>{{ $t("error.filter.state.offline") }}</span>
                </div>

              </div>
              <div class="flex items-center flex-shrink-0 lg:hidden 2xl:flex">
                <BellIcon v-if="hasNetworkAlert" class="w-8 h-8 px-1 mx-1 rounded-full stroke-red-800" aria-hidden="true" />
                <DeviceMobileIcon v-else class="w-8 h-8 px-1 mx-1 rounded-full" aria-hidden="true" />
              </div>

            </div>
            <div class="flex justify-center flex-1 w-full mt-0 text-gray-400 rotate-180 2xl:mt-2">
              <svg data-accordion-icon class="w-3 h-3 shrink-0" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5 5 1 1 5" />
              </svg>
            </div>
          </div>

          <div id="accordion-open-body-network" class="hidden pb-0" aria-labelledby="accordion-open-heading-network">
            <p id='tooltipTriggerModemLink' class='flex mb-1'>
              <span class='w-6 h-6 mr-2 font-medium lg:w-4 lg:h-4 2xl:w-6' v-html="getIconOf('network', 'modem_link', device)"></span>
              <span class='flex items-center text-sm font-light hover:underline hover:cursor-default lg:text-xs 2xl:text-sm'>{{ $t("alarms.modem_link") }}</span>
            </p>
            <AlarmTooltip :targetId="'tooltipTriggerModemLink'" type="network" alert="modem_link" :device="device" />

            <p id='tooltipTriggerModemConQuality' class='flex mb-1'>
              <span class='w-6 h-6 mr-2 font-medium lg:w-4 lg:h-4 2xl:w-6' v-html="getIconOf('network', 'modem_connection_quality', device)"></span>
              <span class='flex items-center text-sm font-light hover:underline hover:cursor-default lg:text-xs 2xl:text-sm'>{{ $t("alarms.modem_connection_quality") }}</span>
            </p>
            <AlarmTooltip :targetId="'tooltipTriggerModemConQuality'" type="network" alert="modem_connection_quality" :device="device" />

            <p id='tooltipTriggerNetCloudRep' class='flex mb-1'>
              <span class='w-6 h-6 mr-2 font-medium lg:w-4 lg:h-4 2xl:w-6' v-html="getIconOf('network', 'network_cloud_reported', device)"></span>
              <span class='flex items-center text-sm font-light hover:underline hover:cursor-default lg:text-xs 2xl:text-sm'>{{ getTitle("network", "network_cloud_reported", device) }}</span>
            </p>
            <AlarmTooltip :targetId="'tooltipTriggerNetCloudRep'" type="network" alert="network_cloud_reported" :device="device" />

            <p v-if="hasArcConfigured" id='tooltipTriggerNetArcRep' class='flex mb-1'>
              <span class='w-6 h-6 mr-2 font-medium lg:w-4 lg:h-4 2xl:w-6' v-html="getIconOf('network', 'network_arc_reported', device)"></span>
              <span class='flex items-center text-sm font-light hover:underline hover:cursor-default lg:text-xs 2xl:text-sm'>{{ $t("alarms.network_arc_reported") }}</span>
            </p>
            <AlarmTooltip v-if="hasArcConfigured" :targetId="'tooltipTriggerNetArcRep'" type="network" alert="network_arc_reported" :device="device" />

          </div>
        </div>
      </div>

      <!-- WIFI -->
      <AlarmTooltip :targetId="'tooltipTriggerHighestWifi'" type="wifi" :alert="getHighestAlert('wifi', device)" :device="device" />

      <div v-show="isConnectedWifi" :class="[
        'rounded-lg overflow overflow-hidden shadow-md px-4 py-2 flex-auto h-full cursor-pointer w-full 2xl:flex-auto 2xl:w-full order-1 2xl:order-none fullHeightWifi',
        ...stateColor(device, 'wifi')]" data-testid="device-status-wifi" ref="wifiStatusContainer">

        <div data-accordion="open">
          <div class="flex flex-wrap justify-between text-gray-800 bg-transparent" id="accordion-open-heading-wifi" data-accordion-target="#accordion-open-body-wifi" aria-expanded="false" aria-controls="accordion-open-body-wifi">
            <div class="flex justify-between w-full">
              <div class="justify-between block">
                <dt class="text-sm truncate lg:text-xs 2xl:text-sm">{{ $t("stateAlarms.label.wifiConnection") }}</dt>

                <div v-if="hasWifiAlert" class="text-sm font-medium lg:font-semibold">
                  <p id='tooltipTriggerHighestWifi' class="flex" v-html="getMostCriticalAlert('wifi', device)"></p>
                </div>
                <div v-else class="text-sm font-medium lg:font-semibold">
                  {{ wifiStatusText(device, $t) }}
                </div>
              </div>
              <div class="flex items-center flex-shrink-0 lg:hidden 2xl:flex">
                <BellIcon v-if="hasWifiAlert" class="w-8 h-8 px-1 mx-1 rounded-full stroke-red-800" aria-hidden="true" />
                <WifiIcon v-else class="w-8 h-8 px-1 mx-1 rounded-full" aria-hidden="true" />
              </div>

            </div>
            <div class="flex justify-center flex-1 w-full mt-0 text-gray-400 rotate-180 2xl:mt-2">
              <svg data-accordion-icon class="w-3 h-3 shrink-0" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5 5 1 1 5" />
              </svg>
            </div>
          </div>

          <div id="accordion-open-body-wifi" class="hidden pb-0" aria-labelledby="accordion-open-heading-wifi">
            <p><br v-if="hasArcConfigured"><br></p>
            <p id='tooltipTriggerWifiLink' class='flex mb-1'>
              <span class='w-6 h-6 mr-2 font-medium lg:w-4 lg:h-4 2xl:w-6' v-html="getIconOf('wifi', 'wifi_link', device)"></span>
              <span class='flex items-center text-sm font-light hover:underline hover:cursor-default lg:text-xs 2xl:text-sm'>{{ $t("alarms.wifi_link") }}</span>
            </p>
            <AlarmTooltip :targetId="'tooltipTriggerWifiLink'" type="wifi" alert="wifi_link" :device="device" />

            <p id='tooltipTriggerWifiConnectionQuality' class='flex mb-1'>
              <span class='w-6 h-6 mr-2 font-medium lg:w-4 lg:h-4 2xl:w-6' v-html="getIconOf('wifi', 'wifi_connection_quality', device)"></span>
              <span class='flex items-center text-sm font-light hover:underline hover:cursor-default lg:text-xs 2xl:text-sm'>{{ $t("alarms.wifi_connection_quality") }}</span>
            </p>
            <AlarmTooltip :targetId="'tooltipTriggerWifiConnectionQuality'" type="wifi" alert="wifi_connection_quality" :device="device" />

          </div>
        </div>
      </div>

      <!-- Power status -->
      <AlarmTooltip :targetId="'tooltipTriggerHighestPower'" type="power" :alert="getHighestAlert('power', device)" :device="device" />

      <div :class="[
        'rounded-lg overflow overflow-hidden shadow-md px-4 py-2 flex-auto h-full cursor-pointer w-full 2xl:flex-auto 2xl:w-full order-1 2xl:order-none fullHeightPower',
        ...stateColor(device, 'power')]" data-testid="device-status-power">
        <div data-accordion="open">

          <div class="flex flex-wrap justify-between text-gray-800 bg-transparent " id="accordion-open-heading-power" data-accordion-target="#accordion-open-body-power" aria-expanded="false" aria-controls="accordion-open-body-power">
            <div class="flex justify-between w-full">
              <div class="justify-between block">
                <dt class="text-sm truncate lg:text-xs 2xl:text-sm">{{ $t("stateAlarms.label.powerSupplyState") }}</dt>

                <div v-if="hasPowerAlert" class="text-sm font-medium lg:font-semibold ">
                  <p id='tooltipTriggerHighestPower' class="flex" v-html="getMostCriticalAlert('power', device)"></p>
                </div>
                <div v-else class="text-sm font-medium lg:font-semibold ">
                  {{ powerSupplyStatusText(device, $t) }}
                </div>
              </div>

              <div class="flex items-center flex-shrink-0 lg:hidden 2xl:flex">
                <BellIcon v-if="hasPowerAlert" class="w-8 h-8 px-1 mx-1 rounded-full stroke-red-800" aria-hidden="true" />
                <BatteryIcon v-else class="w-8 h-8 px-1 mx-1 rounded-full" aria-hidden="true" />
              </div>
            </div>
            <div class="flex justify-center flex-1 w-full mt-0 text-gray-400 rotate-180 2xl:mt-2">
              <svg data-accordion-icon class="w-3 h-3 shrink-0" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5 5 1 1 5" />
              </svg>
            </div>
          </div>
          <div id="accordion-open-body-power" class="hidden pb-0" aria-labelledby="accordion-open-heading-power">
            <p><br v-if="hasArcConfigured"><br></p>
            <p id='tooltipTriggerPowerSupply' class='flex mb-1'>
              <span class='w-6 h-6 mr-2 font-medium lg:w-4 lg:h-4 2xl:w-6' v-html="getIconOf('power', 'power_supply', device)"></span>
              <span class='flex items-center text-sm font-light hover:underline hover:cursor-default lg:text-xs 2xl:text-sm'>{{ $t("alarms.power_supply") }}</span>
            </p>
            <AlarmTooltip :targetId="'tooltipTriggerPowerSupply'" type="power" alert="power_supply" :device="device" />

            <p id='tooltipTriggerBatteryFault' class='flex mb-1'>
              <span class='w-6 h-6 mr-2 font-medium lg:w-4 lg:h-4 2xl:w-6' v-html="getIconOf('power', 'battery_fault', device)"></span>
              <span class='flex items-center text-sm font-light hover:underline hover:cursor-default lg:text-xs 2xl:text-sm'>{{ $t("alarms.battery_fault") }}</span>
            </p>
            <AlarmTooltip :targetId="'tooltipTriggerBatteryFault'" type="power" alert="battery_fault" :device="device" />

          </div>
        </div>
      </div>

      <!-- Accessory status -->
      <AlarmTooltip :targetId="'tooltipTriggerHighestAccessories'" type="accessories" :alert="getHighestAlert('accessories', device)" :device="device" />

      <div :class="[
        'rounded-lg overflow overflow-hidden shadow-md px-4 py-2 flex-auto h-full cursor-pointer w-full 2xl:flex-auto 2xl:w-full order-1 2xl:order-none fullHeightAccess',
        ...stateColor(device, 'accessories')
      ]" data-testid="device-status-accessories">
        <div data-accordion="open">
          <div class="flex flex-wrap justify-between text-gray-800 bg-transparent" id="accordion-open-heading-accessory" data-accordion-target="#accordion-open-body-accessory" aria-expanded="false" aria-controls="accordion-open-body-accessory">
            <div class="flex justify-between w-full">
              <div class="justify-between block">
                <dt class="text-sm truncate lg:text-xs 2xl:text-sm">{{ $t("stateAlarms.label.braceletPresenceState") }}</dt>

                <div v-if="hasAccessoryAlert" class="text-sm font-medium lg:font-semibold ">
                  <p id="tooltipTriggerHighestAccessories" class="flex" v-html="getMostCriticalAlert('accessories', device)"></p>
                </div>
                <div v-else class="text-sm font-medium lg:font-semibold"> {{ braceletPresenceStatusText(device, $t) }} </div>
              </div>

              <div class="flex items-center flex-shrink-0 lg:hidden 2xl:flex">
                <BellIcon v-if="hasAccessoryAlert" class="w-8 h-8 px-1 mx-1 rounded-full stroke-red-800" aria-hidden="true" />
                <BraceletIcon v-else class="w-8 h-8 px-1 mx-1 rounded-full" aria-hidden="true" />
              </div>
            </div>
            <div class="flex justify-center flex-1 w-full mt-0 text-gray-400 rotate-180 2xl:mt-2">
              <svg data-accordion-icon class="w-3 h-3 shrink-0" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5 5 1 1 5" />
              </svg>
            </div>
          </div>
          <div id="accordion-open-body-accessory" class="hidden pb-0" aria-labelledby="accordion-open-heading-accessory">
            <p><br v-if="hasArcConfigured"><br></p>
            <p id='tooltipTriggerBraceletPresence' class='flex mb-1'>
              <span class='w-6 h-6 mr-2 font-medium lg:w-4 lg:h-4 2xl:w-6' v-html="getIconOf('accessories', 'bracelet_presence', device)"></span>
              <span class='flex items-center text-sm font-light hover:underline hover:cursor-default lg:text-xs 2xl:text-sm'>{{ $t("alarms.bracelet_presence") }}</span>
            </p>
            <AlarmTooltip :targetId="'tooltipTriggerBraceletPresence'" type="accessories" alert="bracelet_presence" :device="device" />

            <p id='tooltipTriggerBraceletBattery' class='flex mb-1'>
              <span class='w-6 h-6 mr-2 font-medium lg:w-4 lg:h-4 2xl:w-6' v-html="getIconOf('accessories', 'bracelet_battery', device)"></span>
              <span class='flex items-center text-sm font-light hover:underline hover:cursor-default lg:text-xs 2xl:text-sm'>{{ $t("alarms.bracelet_battery") }}</span>
            </p>
            <AlarmTooltip :targetId="'tooltipTriggerBraceletBattery'" type="accessories" alert="bracelet_battery" :device="device" />

          </div>
        </div>
      </div>


      <div :class="[
        'rounded-lg overflow overflow-hidden shadow-md px-4 py-2 flex-auto justify-between relative h-full cursor-pointer w-full 2xl:flex-auto 2xl:w-full order-1 2xl:order-none',
        ...dailyCheckInStatusColor(device),
      ]" data-testid="device-status-daily-checkin">
        <div class="flex justify-between ">
          <div class="justify-between block w-full sm:flex md:block">
            <dt class="text-sm truncate lg:text-xs 2xl:text-sm">{{ $t("device.general.dailyCheckIn") }}</dt>
            <dd class="text-sm font-medium lg:font-semibold lg:text-xs 2xl:text-sm"> {{ dailyCheckInStatusText(device, $t) }} </dd>
          </div>
          <div class="items-center flex-shrink-0 hidden md:flex">
            <CalendarIcon :class="['h-8', 'w-8', 'rounded-full', ...dailyCheckInStatusColor(device), 'px-1']" aria-hidden="true" />
          </div>
        </div>
      </div>
    </dl>

  </div>

  <ChangeTenant :open="showChangeTenantModal" :tenants="tenants" :current-tenant="device.owner" @confirm="handleTenantChange" @cancel="showChangeTenantModal = false" />
  <SimpleAlert @cancel="confirmOpen = false" @approve="save" :open="confirmOpen" :title="$t('device.header.changeTenant')" :description="$t('device.header.sureTenantChange')" :action="$t('common.action.apply')"></SimpleAlert>

</template>

<script>
import {
  stateColor,
  dailyCheckInStatusColor,

} from "@/lib/statusColors";
import { hasAlert, getMostCriticalAlert, hasWifiConnection, getIconOf, getHighestAlert, getTitle } from "@/lib/alarms";
import { CalendarIcon, WifiIcon, DeviceMobileIcon, BellIcon, PencilIcon } from "@heroicons/vue/outline";
import { BraceletIcon } from "@/components/icons";
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { onUnmounted } from "vue";
import { BatteryIcon } from "./icons";
import { braceletPresenceStatusText, wifiStatusText, dailyCheckInStatusText, powerSupplyStatusText, getOnlineStatus } from "@/lib/statusTexts";
import AlarmTooltip from "@/components/tooltips/AlarmTooltip.vue";
import { hasDeviceTenantPermission, USER_PERMISSIONS } from "@/lib/userHasPermission";
import ChangeTenant from "@/components/modals/ChangeTenant.vue";
import SimpleAlert from "@/components/modals/SimpleAlert.vue";
import { useI18n } from "vue-i18n";
import LoadingSpinner from "./LoadingSpinner.vue";
import mccMncList from 'mcc-mnc-list';
import { formatUptimeNumber, uptimeSummary, utcMonthName } from "@/lib/uptime";

export default {
  name: "DeviceStates",
  methods: {
    braceletPresenceStatusText,
    powerSupplyStatusText,
    stateColor,
    dailyCheckInStatusColor,
    dailyCheckInStatusText,
    hasAlert,
    getMostCriticalAlert,
    getTitle,
    getHighestAlert,
    hasWifiConnection,
    wifiStatusText,
    getIconOf,
    getOnlineStatus,
  },
  components: {
    CalendarIcon,
    BraceletIcon,
    BatteryIcon,
    WifiIcon,
    BellIcon,
    DeviceMobileIcon,
    AlarmTooltip,
    PencilIcon,
    ChangeTenant,
    SimpleAlert,
    LoadingSpinner
  },

  setup() {
    const { t, locale } = useI18n();

    const store = useStore();
    const device = computed(() => store.state.device.current);

    const hasNetworkAlert = computed(() => hasAlert('network', device.value))
    const hasPowerAlert = computed(() => hasAlert('power', device.value))
    const hasAccessoryAlert = computed(() => hasAlert('accessories', device.value))
    const hasWifiAlert = computed(() => hasAlert("wifi", device.value))
    const isConnectedWifi = computed(() => hasWifiConnection(device.value))
    const uptime = computed(() => {
      const stored = store.state.device.uptime;
      return stored && stored.serialNumber === device.value.serialNumber ? uptimeSummary(stored) : null;
    });
    const formatNumber = (value) => formatUptimeNumber(value, locale.value);
    const uptimeMonthName = computed(() => utcMonthName(locale.value));
    const uptimeTooltip = computed(() => {
      if (!uptime.value) {
        return t("device.header.uptimeNoData");
      }
      return t("device.header.uptimeTooltip", {
        hours: formatNumber(uptime.value.hours),
        month: uptimeMonthName.value,
        percent: formatNumber(uptime.value.percent),
      });
    });

    const tenants = computed(() => store.state.allTenants || []);
    const isAdmin = computed(() => hasDeviceTenantPermission(USER_PERMISSIONS.ADMIN));
    const canChangeTenant = computed(() => isAdmin.value && tenants.value.length > 1);
    const showChangeTenantModal = ref(false);
    const confirmOpen = ref(false);
    const selectedTenant = ref(null);
    const isLoadingTenantChange = ref(false);
    let tenantChangeInterval = null;

    const hasArcConfigured = computed(() => {
      return device.value.configV2.alarm_app.destinations.some(destination => destination.flow === 'scaip');
    });

    const getDeviceId = (device) => {
      return [... new Set(device.configV2.alarm_app.destinations.filter(destination => destination.scaip).map(destination => destination.scaip.controller_id))].join(", ")
    };

    const getConnectionStatus = (device) => {
      const networkInfo = device?.stateAlarms?.device_state?.network?.modem?.connected_network;

      if (!networkInfo) {
        return "Unbekannt";
      }

      const { operator_code, access_technology } = networkInfo;
      let operatorName = operator_code || "Unbekannt";

      if (operator_code && operator_code.length >= 4) {
        const mcc = operator_code.substring(0, 3);
        const mnc = operator_code.substring(3);

        const record = mccMncList.all().find(
          r => r.mcc === mcc && r.mnc === mnc
        );

        if (record && (record.brand || record.operator)) {
          operatorName = record.brand || record.operator;
        }
      }

      const tech = access_technology ? access_technology : "";

      return `${operatorName} (${tech})`;
    };

    const snakeToCamel = (str) =>
      str.toLowerCase().replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace("-", "").replace("_", ""));


    const openChangeTenantModal = () => {
      if (canChangeTenant.value) {
        showChangeTenantModal.value = true;
      }
    };

    const handleTenantChange = (newTenant) => {
      selectedTenant.value = newTenant;
      showChangeTenantModal.value = false;
      confirmOpen.value = true;
    };


    const save = async () => {
      const originalOwnerName = device.value.owner?.name;
      isLoadingTenantChange.value = true;

      const ret = await store.dispatch("device/updateOwner", {
        deviceId: device.value.id,
        tenant: selectedTenant.value,
      });
      confirmOpen.value = false;
      if (ret) {

        tenantChangeInterval = setInterval(async () => {
          await store.dispatch("device/reloadDeviceOwner", device.value.serialNumber);
          const currentOwnerName = device.value.owner?.name;

          if (currentOwnerName !== originalOwnerName && currentOwnerName === selectedTenant.value.name) {
            clearInterval(tenantChangeInterval);
            tenantChangeInterval = null;
            isLoadingTenantChange.value = false;
          }
        }, 5000);

        await store.dispatch("showNotification", {
          title: t("notifications.mutations.success"),
          text: t("notifications.mutations.delivering"),
          type: "info",
        });
      } else {
        isLoadingTenantChange.value = false;
      }
    };

    onUnmounted(() => {
      if (tenantChangeInterval) {
        clearInterval(tenantChangeInterval);
        tenantChangeInterval = null;
        isLoadingTenantChange.value = false;
      }
    });


    return {
      device,
      snakeToCamel,
      getDeviceId,
      getConnectionStatus,
      hasNetworkAlert,
      hasPowerAlert,
      hasAccessoryAlert,
      isConnectedWifi,
      uptime,
      uptimeMonthName,
      uptimeTooltip,
      formatNumber,
      hasWifiAlert,
      hasArcConfigured,
      canChangeTenant,
      tenants,
      showChangeTenantModal,
      openChangeTenantModal,
      handleTenantChange,
      confirmOpen,
      selectedTenant,
      save,
      isLoadingTenantChange
    };
  },
};
</script>
<style>
.fullHeightNetwork:not(:has(#accordion-open-body-network.hidden)),
.fullHeightPower:not(:has(#accordion-open-body-power.hidden)),
.fullHeightAccess:not(:has(#accordion-open-body-accessory.hidden)),
.fullHeightWifi:not(:has(#accordion-open-body-wifi.hidden)) {
  height: unset;
  min-height: 100% !important;
}
</style>
